import { formatSignalHeadline } from './signal_format'

/**
 * Activity item -> display shape (icon, title, subtitle, status), for every
 * activity type the account activity endpoint can emit.
 */

function isTaskOpen(it: any) {
  return it?.data?.status === 'open'
}

function isReachoutAwaitingApproval(it: any) {
  return it?.data?.status === 'pending_approval'
}

export function isSequenceStepEvent(it: any) {
  return it?.data?.event_type === 'sequence_step'
}

export function isEmailSwappedEvent(it: any) {
  return it?.data?.event_type === 'email_swapped'
}

export function isSequenceEnrollEvent(it: any) {
  return it?.data?.event_type === 'sequence_enroll'
}

export function isActionEvent(it: any) {
  return it?.data?.event_type === 'action'
}

function formatCustomObjectName(objectName: string): string {
  return String(objectName || '').replace(/__c$/, '').replace(/_/g, ' ')
}

/** Gmail sync lands replies as signals; they read as email, not as a signal. */
function gmailEmailDirection(it: any): 'in' | 'out' | null {
  const name = String(it?.data?.name || '')
  if (name === 'gmail.inbound_email') return 'in'
  if (name === 'gmail.outbound_email') return 'out'
  return null
}

export function timelineIconKey(it: any): string {
  const type = String(it?.type || '').trim().toLowerCase()
  if (type === 'signal' && gmailEmailDirection(it)) return 'email'
  if (type === 'signal') {
    const name = String(it?.data?.name || '')
    if (name === 'jobchange.arrival') return 'arrival'
    if (name === 'jobchange.departure') return 'departure'
    return 'signal'
  }
  if (type === 'story') return 'story'
  if (type === 'task') return 'task'
  if (type === 'reachout_email') return 'email'
  if (type === 'reachout_call') return 'phone_call'
  if (type === 'reachout_voicemail') return 'voicemail'
  if (type === 'reachout_linkedin_connect') return 'linkedin'
  if (type === 'followup') return 'followup'
  if (type === 'crm_update') return 'crm_update'
  if (type.startsWith('salesforce_email')) return 'salesforce_email'
  if (type === 'salesforce_event') return 'salesforce_event'
  if (type === 'salesforce_campaign_member') return 'salesforce_campaign_member'
  if (type === 'salesforce_custom_object') return 'timeline'
  if (type === 'salesforce_opportunity') return 'opportunity'
  if (type.startsWith('hubspot_email')) return 'email'
  if (type === 'hubspot_call') return 'phone_call'
  if (type === 'hubspot_meeting') return 'calendar'
  if (type === 'hubspot_task') return 'task'
  if (type === 'hubspot_note') return 'note'
  if (type === 'hubspot_engagement') return 'timeline'
  if (type.startsWith('pipedrive_email')) return 'email'
  if (type === 'pipedrive_call') return 'phone_call'
  if (type === 'pipedrive_meeting') return 'calendar'
  if (type === 'pipedrive_task') return 'task'
  if (type === 'pipedrive_activity') return 'timeline'
  if (type === 'gong_call') return 'gong'
  if (type === 'call_log') return 'phone_call'
  if (type === 'prospect_lifecycle_transition') return 'lifecycle'
  if (type === 'sales_play_status_change') {
    if (isEmailSwappedEvent(it)) return 'email'
    if (isSequenceStepEvent(it) || isSequenceEnrollEvent(it)) return 'sequence'
    if (isActionEvent(it)) return 'action'
    return 'sales_play'
  }
  return 'timeline'
}

function timelineEntityIconProps(it: any): Record<string, any> {
  const type = String(it?.type || '').trim().toLowerCase()
  if (type === 'signal') {
    const emailDirection = gmailEmailDirection(it)
    if (emailDirection) {
      return { iconKey: 'email', iconColor: emailDirection === 'in' ? '#3b82f6' : '#94a3b8' }
    }
    const iconType = String(it?.signal_icon_type || it?.data?.signal_icon_type || '').trim().toLowerCase()
    const iconName = String(it?.signal_icon_name || it?.data?.signal_icon_name || '').trim()
    const iconStyle = String(it?.signal_icon_style || it?.data?.signal_icon_style || 'solid').trim()
    const iconColor = String(it?.signal_icon_color || it?.data?.signal_icon_color || '#d97706').trim()
    if (iconType === 'favicon' && iconName) return { domain: iconName, iconType: 'image' }
    if (iconName) return { iconType: 'hero', iconName, iconStyle, iconColor }
    return { iconKey: 'signal', iconColor: '#d97706' }
  }
  if (type === 'story') {
    const iconName = String(it?.data?.agent_icon_name || '').trim()
    if (iconName) {
      return {
        iconType: 'hero',
        iconName,
        iconStyle: String(it?.data?.agent_icon_style || 'outline').trim(),
        iconColor: String(it?.data?.agent_icon_color || '#6b7280').trim(),
      }
    }
    return { iconKey: 'story', iconColor: '#8b5cf6' }
  }
  if (type === 'task') return { iconKey: 'task', iconColor: isTaskOpen(it) ? '#f59e0b' : '#94a3b8' }
  if (type === 'reachout_email') return { iconKey: 'email', iconColor: isReachoutAwaitingApproval(it) ? '#3b82f6' : '#94a3b8' }
  if (type === 'reachout_call') return { iconKey: 'phone_call', iconColor: isReachoutAwaitingApproval(it) ? '#22c55e' : '#94a3b8' }
  if (type === 'reachout_voicemail') return { iconKey: 'voicemail', iconColor: isReachoutAwaitingApproval(it) ? '#8b5cf6' : '#94a3b8' }
  if (type === 'reachout_linkedin_connect') return { domain: 'linkedin.com', iconType: 'image' }
  if (type === 'followup') {
    const iconName = String(it?.data?.agent_icon_name || '').trim()
    if (iconName) {
      return {
        iconType: 'hero',
        iconName,
        iconStyle: String(it?.data?.agent_icon_style || 'outline').trim(),
        iconColor: String(it?.data?.agent_icon_color || '#6b7280').trim(),
      }
    }
    return { iconKey: 'followup', iconColor: '#0ea5e9' }
  }
  if (type === 'crm_update') return { iconKey: 'crm_update', iconColor: String(it?.data?.status || '').trim().toLowerCase() === 'pending_approval' ? '#8b5cf6' : '#64748b' }
  if (type === 'salesforce_email_in' || type === 'salesforce_email') return { iconKey: 'salesforce_email', iconColor: '#3b82f6' }
  if (type === 'salesforce_email_out') return { iconKey: 'salesforce_email', iconColor: '#94a3b8' }
  if (type === 'salesforce_event') return { iconKey: 'salesforce_event', iconColor: '#06b6d4' }
  if (type === 'salesforce_campaign_member') return { iconKey: 'salesforce_campaign_member', iconColor: '#10b981' }
  if (type === 'salesforce_custom_object') return { domain: 'salesforce.com', iconType: 'image' }
  if (type === 'salesforce_opportunity') return { iconKey: 'opportunity', iconColor: '#ca8a04' }
  if (type === 'hubspot_email_in') return { iconKey: 'email', iconColor: '#f97316' }
  if (type === 'hubspot_email_out') return { iconKey: 'email', iconColor: '#94a3b8' }
  if (type === 'hubspot_call') return { iconKey: 'phone_call', iconColor: '#f97316' }
  if (type === 'hubspot_meeting') return { iconKey: 'calendar', iconColor: '#f97316' }
  if (type === 'hubspot_task') return { iconKey: 'task', iconColor: '#f97316' }
  if (type === 'hubspot_note') return { iconKey: 'note', iconColor: '#f97316' }
  if (type === 'hubspot_engagement') return { iconKey: 'timeline', iconColor: '#f97316' }
  if (type === 'pipedrive_email_in') return { iconKey: 'email', iconColor: '#16a34a' }
  if (type === 'pipedrive_email_out') return { iconKey: 'email', iconColor: '#94a3b8' }
  if (type === 'pipedrive_call') return { iconKey: 'phone_call', iconColor: '#16a34a' }
  if (type === 'pipedrive_meeting') return { iconKey: 'calendar', iconColor: '#16a34a' }
  if (type === 'pipedrive_task') return { iconKey: 'task', iconColor: '#16a34a' }
  if (type === 'pipedrive_activity') return { iconKey: 'timeline', iconColor: '#16a34a' }
  if (type === 'gong_call') return { domain: 'gong.io', iconType: 'image' }
  if (type === 'call_log') return { iconKey: 'phone_call', iconColor: '#0ea5e9' }
  if (type === 'prospect_lifecycle_transition') return { iconKey: 'lifecycle', iconColor: '#8b5cf6' }
  if (type === 'sales_play_status_change') {
    if (isSequenceStepEvent(it)) return { iconKey: 'sequence', iconColor: '#94a3b8' }
    if (isSequenceEnrollEvent(it)) return { iconKey: 'sequence', iconColor: '#0ea5e9' }
    if (isEmailSwappedEvent(it)) return { iconKey: 'email', iconColor: '#0ea5e9' }
    if (isActionEvent(it)) return { iconKey: 'action', iconColor: '#0ea5e9' }
    return { iconKey: 'sales_play', iconColor: '#0ea5e9' }
  }
  return { iconKey: 'timeline', iconColor: '#64748b' }
}

export function timelineIconColor(it: any): string {
  const props = timelineEntityIconProps(it)
  return props.iconColor || '#94a3b8'
}

export function activityTypeTitle(type: string, it?: any): string {
  const normalized = String(type || '').trim().toLowerCase()
  if (normalized === 'signal') {
    const direction = gmailEmailDirection(it)
    if (direction) return direction === 'in' ? 'Email received' : 'Email sent'
    return 'Signal'
  }
  if (normalized === 'story') return 'Story'
  if (normalized === 'task') return 'Task'
  if (normalized.startsWith('reachout_')) return 'Reachout'
  if (normalized === 'salesforce_email_in') return 'Email received'
  if (normalized === 'salesforce_email_out') return 'Email sent'
  if (normalized === 'salesforce_email') return 'Email'
  if (normalized === 'salesforce_event') return 'Event'
  if (normalized === 'salesforce_campaign_member') return 'Campaign'
  if (normalized === 'salesforce_custom_object') {
    const raw = String(it?.data?.object_name || '').trim()
    return raw ? formatCustomObjectName(raw) : 'Salesforce record'
  }
  if (normalized === 'salesforce_opportunity') return 'Opportunity'
  if (normalized === 'hubspot_email_in') return 'Email received'
  if (normalized === 'hubspot_email_out') return 'Email sent'
  if (normalized === 'hubspot_call') return 'Call'
  if (normalized === 'hubspot_meeting') return 'Meeting'
  if (normalized === 'hubspot_task') return 'Task'
  if (normalized === 'hubspot_note') return 'Note'
  if (normalized === 'hubspot_engagement') return 'Activity'
  if (normalized === 'pipedrive_email_in') return 'Email received'
  if (normalized === 'pipedrive_email_out') return 'Email sent'
  if (normalized === 'pipedrive_call') return 'Call'
  if (normalized === 'pipedrive_meeting') return 'Meeting'
  if (normalized === 'pipedrive_task') return 'Task'
  if (normalized === 'pipedrive_activity') return 'Activity'
  if (normalized === 'call_log') return 'Call'
  if (normalized === 'prospect_lifecycle_transition') return 'Lifecycle change'
  if (normalized === 'sales_play_status_change') return 'Sales play update'
  return String(type || 'Activity')
}

export function timelineItemStatus(it: any): string {
  if (it?.type === 'task') return it?.data?.status
  if (it?.type === 'reachout_email' || it?.type === 'reachout_call' || it?.type === 'reachout_linkedin_connect' || it?.type === 'reachout_voicemail') return it?.data?.status
  if (it?.type === 'followup') return it?.data?.status
  if (it?.type === 'crm_update') return it?.data?.status
  return ''
}

export function timelineItemStatusLabel(it: any): string {
  return timelineItemStatus(it)
}

function formatSignalLine(it: any): string {
  return formatSignalHeadline({ name: it?.data?.name, description: it?.data?.description || {} })
}

export function timelineItemTitle(it: any): string {
  if (it?.type === 'signal') {
    const name = String(it?.data?.name || '')
    if (name === 'gmail.inbound_email' || name === 'gmail.outbound_email') {
      const subject = String(it?.data?.description?.subject || '').trim()
      return subject || (name === 'gmail.outbound_email' ? 'Outbound Email' : 'Inbound Email')
    }
    if (name === 'jobchange.arrival') {
      if (it.data.source_type === 'sfdc_champions_search') {
        return "Champion arrived"
      } else if (it.data.source_type === 'hubspot_champions_search') {
        return "Champion arrived"
      }  
      return "Contact arrived"
    }
    if (name === 'jobchange.departure') {
      if (it.data.source_type === 'sfdc_champions_search') {
        return "Champion left"
      } else if (it.data.source_type === 'hubspot_champions_search') {
        return "Champion left"
      }  
      return "Contact left"
    }
    return formatSignalLine(it)
  }
  if (it?.type === 'story') return String(it?.data?.agent_name || 'Agent Memory')
  if (it?.type === 'task') return String(it?.data?.summary || it?.data?.task_type || activityTypeTitle(it?.type) || 'Task')
  if (it?.type === 'followup') return String(it?.data?.agent_name || 'Followup')
  if (it?.type === 'crm_update') return String(it?.data?.title || 'CRM update')
  if (it?.type === 'reachout_email') return String(it?.data?.subject || '').trim() || 'Email'
  if (it?.type === 'reachout_call') return 'Call'
  if (it?.type === 'reachout_voicemail') return 'Voicemail'
  if (it?.type === 'reachout_linkedin_connect') return 'LI Connect'
  if (it?.type === 'salesforce_email_in' || it?.type === 'salesforce_email_out' || it?.type === 'salesforce_email') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || activityTypeTitle(it?.type) || 'Email'
  if (it?.type === 'salesforce_event') return String(it?.data?.subject || '').trim() || 'Event'
  if (it?.type === 'salesforce_campaign_member') return String(it?.data?.campaign_name || '').trim() || 'Campaign activity'
  if (it?.type === 'salesforce_custom_object') {
    const name = String(it?.data?.Name || it?.data?.name || '').trim()
    return name || formatCustomObjectName(String(it?.data?.object_name || '')) || 'Salesforce record'
  }
  if (it?.type === 'salesforce_opportunity') return String(it?.data?.Name || '').trim() || 'Opportunity'
  if (it?.type === 'hubspot_email_in' || it?.type === 'hubspot_email_out') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || activityTypeTitle(it?.type)
  if (it?.type === 'hubspot_call') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Call'
  if (it?.type === 'hubspot_meeting') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Meeting'
  if (it?.type === 'hubspot_task') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Task'
  if (it?.type === 'hubspot_note') return 'Note'
  if (it?.type === 'hubspot_engagement') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Activity'
  if (it?.type === 'pipedrive_email_in' || it?.type === 'pipedrive_email_out') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || activityTypeTitle(it?.type)
  if (it?.type === 'pipedrive_call') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Call'
  if (it?.type === 'pipedrive_meeting') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Meeting'
  if (it?.type === 'pipedrive_task') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || 'Task'
  if (it?.type === 'pipedrive_activity') return String(it?.data?.clean_subject || it?.data?.subject || '').trim() || activityTypeTitle(it?.type)
  if (it?.type === 'gong_call') return String(it?.data?.title || '').trim() || 'Gong call'
  if (it?.type === 'call_log') return String(it?.data?.disposition_label || it?.data?.disposition_code || '').trim() || 'Call'
  if (it?.type === 'prospect_lifecycle_transition') {
    const from = String(it?.data?.from_state || '').trim()
    const to = String(it?.data?.to_state || '').trim()
    if (from && to) return `${from} → ${to}`
    return to || 'Lifecycle change'
  }
  if (it?.type === 'sales_play_status_change') {
    const kind = String(it?.data?.kind || '').trim()
    const playName = String(it?.data?.sales_play_name || '').trim()
    const label = kind ? kind.replace(/_/g, ' ') : 'Status change'
    return playName ? `${label} · ${playName}` : label
  }
  return activityTypeTitle(it?.type, it) || String(it?.type || 'Activity')
}

export function timelineItemSubtitle(it: any): string {
  return formatTimelineLine(it) || String(it?.type || 'Activity')
}

function formatTimelineLine(it: any): string {
  if (!it) return ''
  if (it.type === 'story') return String(it.data?.sentence || '')
  if (it.type === 'task') return String(it.data?.summary || it.data?.task_type || '')
  if (it.type === 'signal') {
    const name = String(it?.data?.name || '')
    if (name === 'gmail.inbound_email') {
      const from = String(it?.data?.description?.from_email || it?.data?.description?.from_header || '').trim()
      return from ? `From ${from}` : 'Inbound Email'
    }
    if (name === 'gmail.outbound_email') {
      const to = String(it?.data?.description?.to || '').trim()
      return to ? `To ${to}` : 'Outbound Email'
    }
    if (name === 'jobchange.arrival' || name === 'jobchange.departure') {
      return it.data.description?.name || ''
    }
    return formatSignalLine(it)
  }
  if (it.type === 'followup') return String(it?.data?.reason || '')
  if (it.type === 'crm_update') return String(it?.data?.subtitle || '')
  if (it.type === 'reachout_email') {
    const to = String(it?.data?.to || '').trim()
    return to ? `To ${to}` : ''
  }
  if (it.type === 'reachout_call') return String(it?.data?.to || '').trim()
  if (it.type === 'reachout_voicemail') return String(it?.data?.to || '').trim()
  if (it.type === 'reachout_linkedin_connect') return String(it?.data?.to || '').trim()
  if (it.type === 'salesforce_email_in' || it.type === 'salesforce_email') {
    const subj = String(it?.data?.clean_subject || it.data?.subject || '').trim()
    const from = String(it?.data?.from || it.data?.who_name || it.data?.who_email || '').trim()
    return from || subj || 'Inbound email'
  }
  if (it.type === 'salesforce_email_out') {
    const to = String(it?.data?.to || it.data?.who_name || it.data?.who_email || '').trim()
    const subj = String(it?.data?.clean_subject || it.data?.subject || '').trim()
    return to || subj || 'Outbound email'
  }
  if (it.type === 'salesforce_event') {
    const subj = String(it?.data?.subject || '').trim()
    const who = String(it?.data?.who_name || it.data?.who_email || '').trim()
    if (subj && who) return `${subj} · with ${who}`
    return subj || who || 'Meeting'
  }
  if (it.type === 'salesforce_campaign_member') {
    const camp = String(it?.data?.campaign_name || '').trim()
    const who = String(it?.data?.contact_name || it.data?.contact_email || '').trim()
    if (camp && who) return `${camp} · ${who}`
    return camp || who || 'Campaign'
  }
  if (it.type === 'salesforce_custom_object') {
    return formatCustomObjectName(String(it?.data?.object_name || '')) || 'Salesforce record'
  }
  if (it.type === 'salesforce_opportunity') {
    const stage = String(it?.data?.StageName || '').trim()
    const amount = it?.data?.Amount
    const amountStr = (typeof amount === 'number' && Number.isFinite(amount))
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
      : ''
    return [stage, amountStr].filter(Boolean).join(' · ') || 'Opportunity'
  }
  if (it.type === 'hubspot_email_in') {
    const who = String(it?.data?.contact_info?.name || it?.data?.from_name || it?.data?.from_email || '').trim()
    return who ? `From ${who}` : 'Email received'
  }
  if (it.type === 'hubspot_email_out') {
    const who = String(it?.data?.contact_info?.name || it?.data?.to_name || it?.data?.to_email || '').trim()
    return who ? `To ${who}` : 'Email sent'
  }
  if (it.type === 'hubspot_call') {
    const who = String(it?.data?.contact_info?.name || '').trim()
    const outcome = String(it?.data?.outcome || '').trim()
    return [who, outcome].filter(Boolean).join(' · ') || 'Call'
  }
  if (it.type === 'hubspot_meeting') {
    const who = String(it?.data?.contact_info?.name || '').trim()
    const outcome = String(it?.data?.outcome || '').trim()
    return [who, outcome].filter(Boolean).join(' · ') || 'Meeting'
  }
  if (it.type === 'hubspot_task') {
    const outcome = String(it?.data?.outcome || '').trim()
    return outcome || 'Task'
  }
  if (it.type === 'hubspot_note') {
    const body = String(it?.data?.body || '').trim()
    return body.length > 80 ? body.slice(0, 80) + '…' : body || 'Note'
  }
  if (it.type === 'hubspot_engagement') {
    const kind = String(it?.data?.engagement_type || '').trim()
    return kind || 'Activity'
  }
  if (it.type === 'pipedrive_email_in') {
    const who = String(it?.data?.contact_info?.name || '').trim()
    if (who) return `From ${who}`
    const from = ((it?.data?.from_ || [])[0])
    const email = String(from?.email_address || from?.name || '').trim()
    return email ? `From ${email}` : 'Email received'
  }
  if (it.type === 'pipedrive_email_out') {
    const who = String(it?.data?.contact_info?.name || '').trim()
    if (who) return `To ${who}`
    const to = ((it?.data?.to || [])[0])
    const email = String(to?.email_address || to?.name || '').trim()
    return email ? `To ${email}` : 'Email sent'
  }
  if (it.type === 'pipedrive_call' || it.type === 'pipedrive_meeting' || it.type === 'pipedrive_task' || it.type === 'pipedrive_activity') {
    const who = String(it?.data?.contact_info?.name || '').trim()
    const note = String(it?.data?.note || it?.data?.public_description || '').trim()
    const clean = note.replace(/<[^>]*>/g, '').trim()
    const detail = clean.length > 80 ? clean.slice(0, 80) + '…' : clean
    return [who, detail].filter(Boolean).join(' · ') || activityTypeTitle(it.type)
  }
  if (it.type === 'gong_call') {
    const direction = String(it?.data?.direction || '').trim()
    const counts = it?.data?.participant_summary?.counts || {}
    const internal = Number(counts?.internal || 0)
    const external = Number(counts?.external || 0)
    const participants = internal || external ? `${internal} internal · ${external} external` : ''
    return [direction, participants].filter(Boolean).join(' · ')
  }
  if (it.type === 'prospect_lifecycle_transition') {
    const reason = String(it?.data?.reason || '').trim()
    return reason || 'Lifecycle transition'
  }
  if (it.type === 'sales_play_status_change') {
    const reason = String(it?.data?.reason || '').trim()
    const detail = String(it?.data?.detail || '').trim()
    const reasonLine = [reason, detail].filter(Boolean).join(': ')
    if (!reasonLine) return 'Sales play status change'
    return it?.data?.actor_user_id ? `manual · ${reasonLine}` : reasonLine
  }
  return ''
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export function timelineItemLine3(it: any): string {
  const name = String(it?.data?.name || '')
  if (name.startsWith("jobchange.")) return it?.data?.description?.title || ''
  return formatTime(it?.created_at)
}



export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    if (d.getFullYear() != new Date().getFullYear()){
      return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' })
    }
    return d.toLocaleDateString([], { month: 'short', day: '2-digit' })
  } catch {
    return ''
  }
}

export function timelineItemKey(it: any): string {
  return String(it?.id || `${it?.type || 'activity'}:${it?.created_at || ''}`)
}
